import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import AuthDialog from '@/components/AuthDialog';
import { Users, Lock, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';

const GroupInviteLink = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  useEffect(() => {
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchPreview = async () => {
    try {
      const res = await axios.get(`${API}/groups/by-token/${token}`);
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid invite link');
    } finally {
      setLoading(false);
    }
  };

  // Auto-join after user logs in (for already-existing users) — runs when `user` becomes available
  useEffect(() => {
    if (user && preview && !joining) {
      // Auto-join immediately if user just logged in via this page
      handleJoin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, preview]);

  const handleJoin = async () => {
    if (!user) {
      setShowAuthDialog(true);
      return;
    }
    setJoining(true);
    try {
      const res = await axios.post(`${API}/groups/by-token/${token}/join`);
      if (res.data.already_member) {
        toast.info('You are already a member of this group');
      } else {
        toast.success(res.data.message || 'Joined the group!');
      }
      navigate(`/groups/${res.data.group_slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join group');
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 pb-20 px-4 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 pb-20 px-4">
          <div className="max-w-md mx-auto">
            <Card className="border-2 border-red-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-bold mb-2">Invitation Unavailable</h2>
                  <p className="text-muted-foreground mb-6">{error}</p>
                  <Button onClick={() => navigate('/groups')} variant="outline" data-testid="invite-back-to-groups">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Groups
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-20 px-4">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="border-2 border-purple-100 overflow-hidden" data-testid="invite-preview-card">
              {/* Hero gradient */}
              <div className="bg-gradient-to-br from-purple-600 to-amber-500 p-8 text-center text-white">
                <div className="mx-auto mb-4 w-24 h-24 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center overflow-hidden">
                  {preview.icon_url ? (
                    <img src={preview.icon_url} alt={preview.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-5xl">{preview.icon || (preview.is_private ? '🔒' : '👥')}</span>
                  )}
                </div>
                <h1 className="text-2xl font-bold mb-2" data-testid="invite-group-name">{preview.name}</h1>
                <div className="flex items-center justify-center gap-2 text-white/90 text-sm">
                  {preview.is_private && (
                    <span className="inline-flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded-full">
                      <Lock className="w-3 h-3" />
                      Private
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {preview.member_count} member{preview.member_count !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              
              <CardContent className="p-6 space-y-5">
                {preview.created_by && (
                  <div className="flex items-center gap-3 bg-purple-50 rounded-lg p-3 border border-purple-100">
                    {preview.created_by.profile_picture ? (
                      <img src={preview.created_by.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold">
                        {preview.created_by.name?.[0] || '?'}
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground">Invited by</p>
                      <p className="font-semibold text-sm">{preview.created_by.name}</p>
                    </div>
                  </div>
                )}
                
                {preview.description && (
                  <p className="text-sm text-muted-foreground leading-relaxed">{preview.description}</p>
                )}
                
                {preview.category && (
                  <div>
                    <span className="inline-block px-3 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-100">
                      {preview.category}
                    </span>
                  </div>
                )}
                
                <div className="border-t pt-5">
                  {user ? (
                    <Button
                      onClick={handleJoin}
                      disabled={joining}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      size="lg"
                      data-testid="join-via-invite-btn"
                    >
                      {joining ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Joining...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Join Group
                        </>
                      )}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => setShowAuthDialog(true)}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        size="lg"
                        data-testid="signup-to-join-btn"
                      >
                        Sign Up to Join
                      </Button>
                      <p className="text-xs text-center text-muted-foreground mt-3">
                        Already have an account? <button type="button" onClick={() => setShowAuthDialog(true)} className="text-purple-600 underline">Log in</button>
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
      <Footer />
      
      <AuthDialog
        open={showAuthDialog}
        onClose={() => setShowAuthDialog(false)}
        inviteToken={token}
        customMessage={`Join "${preview.name}" — sign up takes less than a minute.`}
        defaultToSignup={true}
      />
    </div>
  );
};

export default GroupInviteLink;
